"use client";

import { c as _c } from "react-compiler-runtime";
import { Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { Text } from "@/components/primitives";
import AppImage from "@/components/primitives/Image/Image";
import ImageCropEditor from "@/components/ui/ImageCropEditor/ImageCropEditor";
import { useAnalytics } from "@/core/analytics";
import { useTranslation } from "@/core/i18n";
import { logger } from "@/core/logging";
import { compressImageFile } from "@/core/media";
import type { DisplayAgelessAssessment } from "@/core/api/orval/model";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { AestheticsPhotoUploader, type PhotoUploaderCopy } from "@/features/assessments/components/ImageCaptureStep/components/AestheticsPhotoUploader/AestheticsPhotoUploader";
import { uploadFrontalFacialImageForAssessment } from "@/features/assessments/components/ImageCaptureStep/hooks/photoApi";
import { CaptureHeader } from "@/features/assessments/components/ImageCaptureStep/stages/components/CaptureHeader";
import type { FrontCaptureResult } from "@/features/assessments/components/ImageCaptureStep/types";
import { CAPTURE_COMPRESS_MAX_SIZE } from "@/features/camera/captureConstants";
const EDITOR_MAX_DIMENSION = CAPTURE_COMPRESS_MAX_SIZE * 2;
const EDITOR_JPEG_QUALITY = 0.92;
// Matches the 3:4 frame LashBrowConfirmStage previews the result in, so what the client crops is
// what they are shown next.
const UPLOAD_CROP_ASPECT_RATIO = 3 / 4;
const IMAGE_PREPARE_TIMEOUT_MS = 30_000;
type UploadPhase = "pickFile" | "preparing" | "cropping" | "uploading" | "error";

/**
 * Lash/brow wording for the shared uploader. The aesthetics default asks for a clear full face,
 * which is the wrong instruction here — this flow wants a close-up of the eye region, so the prompt,
 * the format hints, and all three guidance tips are swapped for the lash/brow intake copy.
 */
const LASH_BROW_PHOTO_UPLOADER_COPY: PhotoUploaderCopy = {
  dropPhoto: "lashBrowSteps.intake.dropPhoto",
  tapToUpload: "lashBrowSteps.intake.tapToUpload",
  tapToUploadMobile: "lashBrowSteps.intake.tapToUploadMobile",
  supportedFormats: "lashBrowSteps.intake.supportedFormats",
  supportedFormatsMobile: "lashBrowSteps.intake.supportedFormatsMobile",
  unsupportedFileType: "lashBrowSteps.intake.unsupportedFileType",
  fileTooLarge: "lashBrowSteps.intake.fileTooLarge",
  tryDifferentPhoto: "lashBrowSteps.intake.tryDifferent",
  bestResults: "lashBrowSteps.intake.bestResults",
  hints: ["lashBrowSteps.intake.tips.frontFacing", "lashBrowSteps.intake.tips.evenLight", "lashBrowSteps.intake.tips.eyesOpen"]
};
type PreparedUpload = {
  imageSrc: string;
  fileName: string;
};
type LashBrowPhotoUploadStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  onComplete: (result: FrontCaptureResult) => void;
  onBackToTips?: () => void;
};

/**
 * Lets a client reach the lash/brow analysis with a photo they already have, rather than only via
 * the live camera — the aesthetics flow has had this since launch and lash/brow shipped without it,
 * which blocked anyone on a device without a usable camera.
 *
 * Deliberately thinner than the aesthetics PhotoUploadFlow: that one pre-screens with the face
 * landmarker because it needs a frontal full-face pose, whereas lash/brow is judged on the eye crop
 * by the server-side lash/brow validation that LashBrowConfirmStage already polls. This stage stops
 * at producing a FrontCaptureResult, so consent, validation, rejection copy, retake, and completion
 * all stay in the stages that already own them.
 */
export function LashBrowPhotoUploadStage(t0) {
  const $ = _c(27);
  const {
    assessment,
    headers,
    onComplete,
    onBackToTips
  } = t0;
  const {
    t
  } = useTranslation();
  const analytics = useAnalytics();
  const {
    flowType
  } = useAssessmentStep();
  const [phase, setPhase] = useState("pickFile");
  const [preparedUpload, setPreparedUpload] = useState(null);
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setPhase("pickFile");
      setPreparedUpload(null);
      setThumbnailUrl(null);
      setUploadError(null);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const resetToPicker = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = message => {
      setUploadError(message);
      setPreparedUpload(null);
      setThumbnailUrl(null);
      setPhase("error");
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const failWithMessage = t2;
  let t3;
  if ($[2] !== t) {
    t3 = async file => {
      setUploadError(null);
      setPhase("preparing");
      ;
      try {
        const editorFile = await withTimeout(compressImageFile(file, EDITOR_MAX_DIMENSION, EDITOR_JPEG_QUALITY), IMAGE_PREPARE_TIMEOUT_MS, "Timed out while preparing the lash/brow photo for cropping.");
        const imageSrc = await readFileAsDataUrl(editorFile);
        setPreparedUpload({
          imageSrc,
          fileName: file.name
        });
        setPhase("cropping");
      } catch (t4) {
        const error = t4;
        logger.error("lash_brow_photo_upload_prepare_failed", undefined, error as Error);
        failWithMessage(t("lashBrowSteps.intake.uploadFailed"));
      }
    };
    $[2] = t;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleFileSelected = t3;
  let t4;
  if ($[4] !== analytics || $[5] !== assessment || $[6] !== flowType || $[7] !== headers || $[8] !== onComplete || $[9] !== preparedUpload || $[10] !== t) {
    t4 = async croppedFile => {
      if (!preparedUpload) {
        logger.error("lash_brow_photo_upload_missing_prepared_file");
        failWithMessage(t("lashBrowSteps.intake.uploadFailed"));
        return;
      }
      setPhase("uploading");
      setUploadError(null);
      ;
      try {
        const fileName = preparedUpload.fileName;
        const fileWithOriginalName = new File([croppedFile], fileName, {
          type: croppedFile.type,
          lastModified: croppedFile.lastModified
        });
        const photoDataUrl = await readFileAsDataUrl(fileWithOriginalName);
        setThumbnailUrl(photoDataUrl);
        const uploadResponse = await uploadFrontalFacialImageForAssessment({
          assessmentId: assessment.id,
          platformId: assessment.platformId,
          photoDataUrl,
          fileName,
          requestOptions: {
            headers
          }
        });
        analytics.trackEvent("assessment_photo_captured", {
          flowType
        });
        onComplete({
          photoData: photoDataUrl,
          uploadedImageId: uploadResponse.imageId
        });
      } catch (t5) {
        const error_0 = t5;
        logger.error("lash_brow_photo_upload_failed", undefined, error_0 as Error);
        failWithMessage(t("lashBrowSteps.intake.uploadFailed"));
      }
    };
    $[4] = analytics;
    $[5] = assessment;
    $[6] = flowType;
    $[7] = headers;
    $[8] = onComplete;
    $[9] = preparedUpload;
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const handleCropComplete = t4;
  const isPickerPhase = phase === "pickFile" || phase === "error";
  const isProgressPhase = phase === "preparing" || phase === "uploading";
  let t5;
  if ($[12] !== onBackToTips) {
    t5 = <CaptureHeader onBack={onBackToTips} />;
    $[12] = onBackToTips;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== handleCropComplete || $[15] !== handleFileSelected || $[16] !== isPickerPhase || $[17] !== isProgressPhase || $[18] !== phase || $[19] !== preparedUpload || $[20] !== t || $[21] !== thumbnailUrl || $[22] !== uploadError) {
    t6 = phase === "cropping" && preparedUpload ? <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-y-contain [padding-bottom:calc(env(safe-area-inset-bottom)+1rem)]"><div className="mx-auto w-full max-w-sm px-3 py-3 sm:px-6 sm:py-4"><ImageCropEditor cancelLabel={t("lashBrowSteps.intake.cancel")} confirmLabel={t("lashBrowSteps.intake.cropAndContinue")} cropAspectRatio={UPLOAD_CROP_ASPECT_RATIO} dragToCropLabel={t("lashBrowSteps.intake.dragToCrop")} imageSrc={preparedUpload.imageSrc} quality={0.85} targetHeight={CAPTURE_COMPRESS_MAX_SIZE} targetWidth={CAPTURE_COMPRESS_MAX_SIZE} tone="overlay" onCancel={resetToPicker} onCropComplete={handleCropComplete} /></div></div> : isProgressPhase ? <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-4"><UploadProgressPreview label={phase === "preparing" ? t("lashBrowSteps.intake.preparingPhoto") : t("lashBrowSteps.intake.uploading")} thumbnailUrl={thumbnailUrl} /></div> : <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-y-auto overscroll-y-contain px-3 py-2 [padding-bottom:calc(env(safe-area-inset-bottom)+0.5rem)] sm:gap-4 sm:px-6"><div className="shrink-0 px-2 text-center"><Text component="p" text={t("lashBrowSteps.intake.uploadIntro")} tone="white" variant="body-sm" weight="light" /></div><AnimatePresence mode="wait">{isPickerPhase ? <motion.div animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} initial={{
          opacity: 0,
          y: 12
        }} key={phase === "error" ? "error" : "picker"} transition={{
          duration: 0.25
        }}><AestheticsPhotoUploader copy={LASH_BROW_PHOTO_UPLOADER_COPY} disabled={false} errorMessage={phase === "error" ? uploadError : null} frame={_temp} onFileSelected={handleFileSelected} onReset={phase === "error" ? resetToPicker : undefined} /></motion.div> : null}</AnimatePresence></div>;
    $[14] = handleCropComplete;
    $[15] = handleFileSelected;
    $[16] = isPickerPhase;
    $[17] = isProgressPhase;
    $[18] = phase;
    $[19] = preparedUpload;
    $[20] = t;
    $[21] = thumbnailUrl;
    $[22] = uploadError;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t5 || $[25] !== t6) {
    t7 = <div className="relative flex min-h-dvh w-full flex-1 flex-col overflow-hidden bg-black/90">{t5}{t6}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
function _temp(isDragging) {
  return <EyeCropUploadFrame isDragging={isDragging} />;
}
type UploadProgressPreviewProps = {
  label: string;
  thumbnailUrl: string | null;
};
function UploadProgressPreview(t0) {
  const $ = _c(10);
  const {
    label,
    thumbnailUrl
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== thumbnailUrl) {
    t3 = <div className="relative aspect-3/4 w-full overflow-hidden rounded-3xl border border-white/15 bg-neutral-900">{thumbnailUrl ? <AppImage fill={true} alt="" className="object-cover" src={thumbnailUrl} /> : <div className="flex h-full w-full items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white/70" /></div>}</div>;
    $[2] = thumbnailUrl;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Loader2 className="h-4 w-4 animate-spin" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== label) {
    t5 = <div className="bg-background/80 flex items-center gap-2 rounded-full px-4 py-2 shadow-lg backdrop-blur-sm">{t4}<Text component="span" text={label} variant="body-sm" weight="medium" /></div>;
    $[5] = label;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <motion.div animate={t1} className="mx-auto flex w-full max-w-sm flex-col items-center justify-center gap-6" initial={t2}>{t3}{t5}</motion.div>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
type EyeCropUploadFrameProps = {
  readonly isDragging: boolean;
};

/**
 * A wide landmark frame standing in for the aesthetics face oval. The oval, with its centre line and
 * eye line, reads as "fit your whole face in here" — the opposite of what this flow needs. This one
 * is a brow-and-eye band so the target framing is obvious before the client picks a file.
 */
function EyeCropUploadFrame(t0) {
  const $ = _c(11);
  const {
    isDragging
  } = t0;
  const borderClass = isDragging ? "border-primary/70" : "border-white/40";
  const glowClass = isDragging ? "shadow-[0_0_24px_hsl(var(--primary)/0.35)]" : "";
  const t1 = `absolute inset-0 rounded-3xl border-2 bg-white/6 transition-colors ${borderClass} ${glowClass}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <div className={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute top-[34%] right-8 left-8 h-px bg-white/25" />;
    t4 = <div className="absolute top-[62%] right-6 left-6 h-px bg-white/20" />;
    t5 = <div className="absolute top-6 bottom-6 left-1/2 w-px -translate-x-1/2 bg-white/15" />;
    t6 = <EyeCropCorner className="-top-2 -left-2 rounded-tl-lg border-t-2 border-l-2" />;
    t7 = <EyeCropCorner className="-top-2 -right-2 rounded-tr-lg border-t-2 border-r-2" />;
    t8 = <EyeCropCorner className="-bottom-2 -left-2 rounded-bl-lg border-b-2 border-l-2" />;
    t9 = <EyeCropCorner className="-right-2 -bottom-2 rounded-br-lg border-r-2 border-b-2" />;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
    $[8] = t9;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
    t8 = $[7];
    t9 = $[8];
  }
  let t10;
  if ($[9] !== t2) {
    t10 = <div aria-hidden={true} className="relative aspect-[16/9] w-[min(80vw,17rem)] shrink-0 sm:w-64">{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}</div>;
    $[9] = t2;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  return t10;
}
type EyeCropCornerProps = {
  readonly className: string;
};
function EyeCropCorner(t0) {
  const $ = _c(2);
  const {
    className
  } = t0;
  const t1 = `absolute h-6 w-6 border-white/55 ${className}`;
  let t2;
  if ($[0] !== t1) {
    t2 = <div className={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    promise.then(value => {
      clearTimeout(timeoutId);
      resolve(value);
    }, (error: unknown) => {
      clearTimeout(timeoutId);
      reject(error);
    });
  });
}